import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { FormDialog } from '@ui/FormDialog';
import { MappingSaveScope } from './element-imports.store';
import {
  loadSaveMappingDialogPrefs,
  saveSaveMappingDialogPrefs,
  type SaveMappingDialogPrefs,
} from './save-mapping-dialog-prefs.ls';
import {
  saveMappingActionLabel,
  type SaveMappingAction,
} from './save-mapping-action.helpers';

export type SaveMappingOptions = {
  scope: MappingSaveScope;
  /** Also materialize the other matching rows in this import. */
  alsoMapMatching: boolean;
};

type SaveMappingDialogProps = {
  onClose: () => void;
  /** Other same-code rows in this import the mapping would also touch. */
  matchingRowCount: number;
  /**
   * Persistable rows this confirm will write. Greater than 1 hides the
   * matching-row checkbox — the selection is already the target set.
   */
  targetCount?: number;
  /** Persist vs overwrite copy for the confirm button. */
  action: SaveMappingAction;
  /** Persists the mapping (+ optional matching rows). Rejects on failure. */
  onSave: (options: SaveMappingOptions) => Promise<void>;
};

const SCOPE_DEFAULT: MappingSaveScope = 'project';
const ALSO_MAP_MATCHING_DEFAULT = true;

const initialPrefs = (): SaveMappingDialogPrefs => {
  const saved = loadSaveMappingDialogPrefs();
  return {
    scope: saved?.scope ?? SCOPE_DEFAULT,
    alsoMapMatching: saved?.alsoMapMatching ?? ALSO_MAP_MATCHING_DEFAULT,
  };
};

/**
 * Scope picker + "also map matching rows" confirm for promoting a resource-code
 * mapping (DEV-3361). Owns its transient form state; the actual persistence is
 * delegated to {@link SaveMappingDialogProps.onSave}. Locks while saving and
 * surfaces a failure inline instead of closing.
 *
 * Mount only while open (the caller conditionally renders it) so each open
 * avoids stale `error` / `isSaving` flash; scope and matching-row choices are
 * restored from {@link loadSaveMappingDialogPrefs}.
 */
export const SaveMappingDialog = ({
  onClose,
  matchingRowCount,
  targetCount = 1,
  action,
  onSave,
}: SaveMappingDialogProps) => {
  const [scope, setScope] = useState<MappingSaveScope>(
    () => initialPrefs().scope,
  );
  const [alsoMapMatching, setAlsoMapMatching] = useState(
    () => initialPrefs().alsoMapMatching,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);

  const showMatching = matchingRowCount > 0 && targetCount <= 1;

  const persistPrefs = (prefs: SaveMappingDialogPrefs) => {
    saveSaveMappingDialogPrefs(prefs);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(false);
    const options = {
      scope,
      alsoMapMatching: showMatching && alsoMapMatching,
    };
    try {
      await onSave(options);
      persistPrefs({ scope, alsoMapMatching });
      onClose();
    } catch {
      setError(true);
      setIsSaving(false);
    }
  };

  // Locked while a save is in flight (Cancel / close / backdrop all no-op).
  const close = () => {
    if (!isSaving) onClose();
  };

  return (
    <FormDialog
      open
      onClose={close}
      title="Apply mapping automatically in future imports?"
      actions={
        <>
          <Button onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isSaving}
            startIcon={
              isSaving ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            {saveMappingActionLabel(action, targetCount)}
          </Button>
        </>
      }
    >
      <RadioGroup
        value={scope}
        onChange={(e) => {
          const nextScope = e.target.value as MappingSaveScope;
          setScope(nextScope);
          persistPrefs({ scope: nextScope, alsoMapMatching });
        }}
      >
        <FormControlLabel
          value="project"
          control={<Radio />}
          label="This project only"
          disabled={isSaving}
        />
        <FormControlLabel
          value="org"
          control={<Radio />}
          label="All projects"
          disabled={isSaving}
        />
      </RadioGroup>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        This action cannot be undone.
      </Typography>
      {showMatching && (
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={alsoMapMatching}
              onChange={(e) => {
                const nextAlsoMapMatching = e.target.checked;
                setAlsoMapMatching(nextAlsoMapMatching);
                persistPrefs({
                  scope,
                  alsoMapMatching: nextAlsoMapMatching,
                });
              }}
              disabled={isSaving}
            />
          }
          label={
            <Box component="span">
              Also update <b>{matchingRowCount}</b> matching{' '}
              {matchingRowCount === 1 ? 'row' : 'rows'} in this import
            </Box>
          }
        />
      )}
      {error && (
        <Typography variant="body2" color="error">
          Could not save the mapping. Please try again.
        </Typography>
      )}
    </FormDialog>
  );
};
